import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { isNullOrUndefined, TypeEntityReference } from '@uzurv/core';
import { UZURV_REST_CONFIGURATION, UzurvRestConfiguration, UzurvRestService } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  PaginatedProviderDetailResponse,
  PaginatedTransitProgramProviderResponse,
  TransitProgram,
  TransitProgramAvailability,
  TransitProgramDetail,
  TransitProgramDriver
} from '../../models';
import { TransitProgramDriverApproval } from '../../models/transit-program-driver-approval';

@Injectable({
  providedIn: 'root'
})
export class TransitProgramService extends UzurvRestService {
  readonly resourceUrl = 'transitPrograms/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Returns a transit program by ID
   *
   * @param id
   * @param overrideCache
   */
  findTransitProgramById(id: number): Observable<TransitProgram> {
    return this.get(`${this.resourceUrl}${id}`).pipe(
      map(response => {
        const transitProgram: TransitProgram = Deserialize(response, TransitProgram);
        return transitProgram;
      })
    );
  }

  /**
   * Returns a transit program by ID
   *
   * @param id
   * @param overrideCache
   */
  findTransitProgramDetailById(id: number): Observable<TransitProgramDetail> {
    return this.get(`${this.resourceUrl}${id}/detail`).pipe(
      map(response => {
        const transitProgram: TransitProgramDetail = Deserialize(response, TransitProgramDetail);
        return transitProgram;
      })
    );
  }

  /**
   *
   * @param zipCode
   * @param radiusMiles
   * @param pageNumber
   * @param pageSize
   */
  listTransitProgramsWithinCoordinates(
    zipCode: string,
    radiusMiles: number,
    pageNumber: number = 0,
    pageSize: number = 20
  ): Observable<Array<TransitProgram>> {
    return this.get<Array<TransitProgram>>(this.resourceUrl, {
      zipCode: zipCode,
      radiusMiles: radiusMiles,
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(
      map(response => {
        if (isNullOrUndefined(response)) {
          return [];
        }

        return Deserialize(response, TransitProgramDetail).sort((a, b) => a.name.localeCompare(b.name));
      })
    );
  }

  /**
   *
   * @param zipCode
   * @param radiusMiles
   * @param pageNumber
   * @param pageSize
   */
  listTransitProgramDetailsWithinCoordinates(
    zipCode: string,
    radiusMiles: number,
    pageNumber: number = 0,
    pageSize: number = 20
  ): Observable<Array<TransitProgramDetail>> {
    return this.get<Array<TransitProgramDetail>>(`${this.resourceUrl}details`, {
      zipCode: zipCode,
      radiusMiles: radiusMiles,
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(
      map(response => {
        if (isNullOrUndefined(response)) {
          return [];
        }

        return Deserialize(response, TransitProgramDetail).sort((a, b) => a.name.localeCompare(b.name));
      })
    );
  }

  /**
   * PATCHs a transit program driver with approved status
   *
   * @param transitProgramDriver
   * @permission driver_management
   */
  patchTransitProgramDriver(transitProgramDriver: TransitProgramDriverApproval): Observable<TransitProgramDriver> {
    return this.patch(
      `${this.resourceUrl}${transitProgramDriver.transitProgramId}/drivers/${transitProgramDriver.driver.id}`,
      Serialize(transitProgramDriver)
    ).pipe(map(response => Deserialize(response, TransitProgramDriver)));
  }

  /**
   * Gets a TransitProgramAvailability object based on zip code.
   * @param zipCode
   */
  getTransitProgramsAvailable(zipCode: string): Observable<TransitProgramAvailability> {
    return this.get<Array<TransitProgramDetail>>(`${this.resourceUrl}transitProgramsAvailable/`, {
      zipCode: zipCode
    }).pipe(map(response => Deserialize(response, TransitProgramAvailability)));
  }

  protected getApiUrl(): string {
    return this.uzurvRestConfiguration.dotsApiUrl;
  }

  listTransitProgramReferences(): Observable<Array<TypeEntityReference>> {
    return this.get<Array<TypeEntityReference>>(`${this.resourceUrl}typeReferences`).pipe(
      map(response => Deserialize(response, TypeEntityReference))
    );
  }
}
